import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowTopRightOnSquareIcon, PlusIcon, MinusIcon } from '@heroicons/react/20/solid';
import { formatDistanceToNow, parseISO } from 'date-fns';

// Mockup for UtilFormatting, replace with actual import
const formatText = (text) => text || "";
const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch (_) {
        return false;
    }
};


const formatRelativeDate = (timestamp) => {
    // Add validation to prevent invalid date errors
    if (!timestamp || isNaN(new Date(timestamp).getTime())) {
        return 'Invalid date';
    }

    try {
        return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Date unavailable';
    }
};


const CompAIStoryBoardItemSlide = ({ newsItem }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCaseStudiesModalOpen, setIsCaseStudiesModalOpen] = useState(false);

    const formattedNewsItem = React.useMemo(() => {
        if (!newsItem) return {};
        return {
            ...newsItem,
            headline: formatText(newsItem.headline),
            summary: formatText(newsItem.summary),
            relevance: formatText(newsItem.relevance),
            impact: formatText(newsItem.impact),
            why: formatText(newsItem.why),
            sales_takeaways: Array.isArray(newsItem.sales_takeaways)
                ? newsItem.sales_takeaways.map(takeaway => formatText(takeaway))
                : [],
        };
    }, [newsItem]);

    if (!newsItem) {
        return <div className="bg-black/20 rounded-xl p-6 text-zinc-400">No news item data.</div>;
    }

    const url = newsItem.url || `https://www.google.com/search?q=${encodeURIComponent(newsItem.headline || '')}`;
    const isGoogleSearch = !newsItem.url;
    const iconSrc = isGoogleSearch
        ? '/util/app/b3-admin-google.png'
        : `https://www.google.com/s2/favicons?domain=${isValidUrl(newsItem.url) ? new URL(newsItem.url).hostname : ''}&sz=64`;
    const sourceName = isGoogleSearch ? 'Google Search' : newsItem.source;

    return (
        <div className="bg-black/20 rounded-xl overflow-hidden p-12 px-32 h-full flex flex-col text-zinc-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-x-12 gap-y-4 flex-grow">
                {/* Left Column: Main Content */}
                <div className="md:col-span-2 flex flex-col">
                    <div className="text-green-300/80 text-2xl font-medium leading-7 pr-3 mb-3">
                        {formattedNewsItem.headline}
                    </div>
                    {formattedNewsItem.why && (
                        <div className="mb-5 mt-6">
                            <h3 className="text-lg font-semibold text-zinc-50 tracking-wider mb-2.5 flex items-center">
                            Potential Opportunity
                            </h3>
                            <div className="text-zinc-300 text-base leading-relaxed mb-5">
                                {formattedNewsItem.why}
                            </div>
                        </div>
                    )}
                    {formattedNewsItem.sales_takeaways && formattedNewsItem.sales_takeaways.length > 0 && (
                        <div className="mb-5 mt-4">
                            <h3 className="text-base font-semibold text-zinc-50 tracking-wider mb-2.5 flex items-center">
                                Solution Takeaways
                            </h3>
                            <div className="space-y-5 mt-8">
                                {formattedNewsItem.sales_takeaways.map((takeaway, i) => (
                                    <div key={i} className="flex items-start gap-5">
                                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black/40 flex items-center justify-center mt-0.5">
                                            <span className="text-lg font-bold text-zinc-400">{i + 1}</span>
                                        </div>
                                        <div className="text-zinc-300 text-base leading-normal">
                                            {takeaway}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                   



                    {/* Case Studies Button (if applicable) - Placed at the bottom of left col */}
                    {newsItem.caseStudies && newsItem.caseStudies.length > 0 && (
                        <div className="mt-auto pt-4">
                            <motion.button
                                onClick={() => setIsCaseStudiesModalOpen(true)}
                                className="w-full bg-black/25 rounded-lg px-3 py-3 hover:bg-black/40 transition-colors text-center border border-zinc-700 hover:border-zinc-600"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                <div className="text-zinc-200 text-sm font-medium">
                                    <span className="text-xl">{newsItem.caseStudies.length}</span>
                                    <br />
                                    <span className="text-zinc-300/80 text-xs">{newsItem.caseStudies.length === 1 ? 'Case Study' : 'Case Studies'} Available</span>
                                </div>
                            </motion.button>
                        </div>
                    )}
                </div>

                {/* Right Column: Source and Accordion */}
                <div className="md:col-span-2 flex flex-col space-y-4">
                    {/* Source information card */}
                    <div className="rounded-lg overflow-hidden bg-black/30 border border-zinc-700/70">
                        <a href={url} target="_blank" rel="noopener noreferrer" className="block p-3 hover:bg-black/40 transition-colors">
                            <div className="flex items-center gap-2.5">
                                <div className="flex-shrink-0 p-1.5 rounded-md bg-black/50 shadow-inner border border-white/10">
                                    <img
                                        src={iconSrc}
                                        alt=""
                                        className="w-5 h-5"
                                        onError={(e) => { e.target.src = '/util/app/default-favicon.png'; }} // Fallback icon
                                    />
                                </div>
                                <div className="flex flex-col flex-grow min-w-0">
                                    <div className="text-zinc-100 text-sm font-medium truncate" title={sourceName}>
                                        {sourceName}
                                    </div>
                                    <div className="text-zinc-400 text-xs mt-0.5">
                                        <span>{newsItem.datePublished ? formatRelativeDate(newsItem.datePublished) : ''}</span>
                                    </div>
                                </div>
                                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                            </div>
                        </a>
                    </div>

                    {/* Accordion for Why Change, Why Us */}
                    <div className="flex-grow flex flex-col pt-4">
                        <div
                            //onClick={() => setIsExpanded(!isExpanded)}
                            className="w-full flex items-center justify-between rounded-lg  px-3 text-green-300/80 text-xl font-semibold transition-all duration-300"
                           // aria-label={isExpanded ? "Hide details" : "Show details"}
                        >
                             Why it Matters
                            
                        </div>


                        <motion.div
 
                            className="overflow-hidden    rounded-lg  "
                        >
                            <div className="p-3 space-y-3">
                                {formattedNewsItem.summary && (
                                    <div>
                                        <div className="text-zinc-300 text-sm leading-normal">
                                            {formattedNewsItem.summary}
                                        </div>
                                    </div>
                                )}
                                {formattedNewsItem.relevance && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-green-300/80 mb-2 mt-6">
                                            Why Change
                                        </h3>
                                        <div className="text-zinc-300 text-sm leading-normal">
                                            {formattedNewsItem.relevance}
                                        </div>
                                    </div>
                                )}
                                {formattedNewsItem.impact && (
                                    <div>
                                        <h3 className="text-lg font-semibold text-green-300/80 mb-2 mt-6">
                                            Why Us
                                        </h3>
                                        <div className="text-zinc-300 text-sm leading-normal">
                                            {formattedNewsItem.impact}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>

                    </div>
                </div>
            </div>

            {/* Case Studies Modal */}
            <AnimatePresence>
                {isCaseStudiesModalOpen && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsCaseStudiesModalOpen(false)}
                    >
                        <motion.div
                            className="bg-zinc-900/95 border border-zinc-700 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl"
                            initial={{ scale: 0.9, y: 10 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 10 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center p-4 md:p-5 border-b border-zinc-800">
                                <h2 className="text-lg font-normal text-zinc-100">Case Studies for: {formattedNewsItem.headline}</h2>
                                <button
                                    onClick={() => setIsCaseStudiesModalOpen(false)}
                                    className="text-zinc-500 hover:text-zinc-300 p-1 rounded-full hover:bg-zinc-700/50 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="p-4 md:p-5 overflow-y-auto">
                                {newsItem.caseStudies && newsItem.caseStudies.length > 0 ? (
                                    <div className="grid grid-cols-1 gap-3 md:gap-4">
                                        {newsItem.caseStudies.map((caseStudy, index) => (
                                            <div key={index} className="bg-black/30 border border-zinc-800 rounded-lg p-3 md:p-4">
                                                <div className="flex justify-between items-start mb-1.5 md:mb-2">
                                                    <h3 className="text-md font-medium text-zinc-100 flex-grow">{caseStudy.name}</h3>
                                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ml-2 flex-shrink-0 ${caseStudy.rating === 'high' ? 'bg-green-500/20 text-green-300 border border-green-500/30' :
                                                            caseStudy.rating === 'medium' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                                                                'bg-red-500/20 text-red-300 border border-red-500/30'
                                                        }`}>
                                                        {caseStudy.rating?.charAt(0).toUpperCase() + caseStudy.rating?.slice(1)} Relevance
                                                    </span>
                                                </div>
                                                <div className="text-zinc-200 text-sm md:text-base mb-1.5 md:mb-2">{caseStudy.biggest_takeaway?.replace(/\.$/, '')}</div>
                                                <p className="text-zinc-400 text-xs md:text-sm leading-normal">{caseStudy.details}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-zinc-500 text-center py-8">No case studies available for this item.</p>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CompAIStoryBoardItemSlide; 